import useSWR from 'swr';
import { createContext, ReactNode } from 'react';

import { Loader } from 'shared/components';
import { ISize } from 'shared/types/Size';
import { getAvailableSizesPrioritizedByCondition } from 'shared/utils/getLowestPriceVariantsPrioritizedByCondtion';
import { getLowestPricedVariantIndex } from 'productTemplate/utils/getLowestPricedVariantIndex';
import { getNewAvailableSizes } from 'productTemplate/utils/getNewAvailableSizes';
import { requestService } from 'shared/utils/services/requestService';
import { useLocation } from 'shared/hooks';
import { useProductTemplate } from 'productTemplate/hooks/useProductTemplate';
import { useSizeConverion } from 'productTemplate/hooks/useSizeConversion';
import { IBuyBarProductSizeOption, IBuyBarProductVariant } from 'shared/types/BuyBarProductVariant';
import { VARIANT_KEYS } from 'productTemplate/enums/variantKeys';

interface IBuyBarVariantsProvider {
  children: ReactNode;
}

interface IBuyBarVariantsContext {
  mapSizeToVariants: Map<number, Map<VARIANT_KEYS, IBuyBarProductVariant>>;
  allSizes: (IBuyBarProductVariant | IBuyBarProductSizeOption)[];
  availableSizes: IBuyBarProductVariant[];
  usedAvailableSizes: IBuyBarProductVariant[];
  newAvailableSizes: IBuyBarProductVariant[];
  centerOnIndex: number | false;
  convertedUserSizeIndex: number | null;
  lowestPricedVariantIndex: number | false;
}

export const BuyBarVariantsContext = createContext<IBuyBarVariantsContext>(null);

const fetchBuyBarData = (url) => requestService({ url, method: 'GET' });

// #TODO: Improve this logic. Want more readable, and more convenient;
const getAllSizes = (sizeOptions: ISize[], availableSizes: IBuyBarProductVariant[]) => {
  return sizeOptions.map((sizeOption) => {
    for (const availableSize of availableSizes) {
      if (sizeOption.value === availableSize.sizeOption.value) {
        return availableSize;
      }
    }
    return { sizeOption } as IBuyBarProductSizeOption;
  });
};

export const BuyBarVariantsProvider = ({ children }: IBuyBarVariantsProvider) => {
  const { id, sizeOptions } = useProductTemplate();
  const { convertedUserSize, isLoading } = useSizeConverion();
  const { selectedCountryCode }: { selectedCountryCode: string } = useLocation();

  const { data } = useSWR(
    () =>
      id
        ? `/web-api/v1/product_variants/buy_bar_data?productTemplateId=${id}&countryCode=${selectedCountryCode}`
        : null,
    fetchBuyBarData,
  );

  if (!data) return <Loader style={{ margin: '50vh 0' }} />;
  const { availableSizes, mapSizeToVariants, usedVariant } =
    getAvailableSizesPrioritizedByCondition(data);

  const convertedUserSizeIndex =
    convertedUserSize && !isLoading
      ? availableSizes.findIndex(
          (size) => String(size?.sizeOption?.value) === String(convertedUserSize?.size),
        )
      : null;
  const lowestPricedVariantIndex = getLowestPricedVariantIndex(availableSizes);
  const centerOnIndex =
    convertedUserSizeIndex !== null && convertedUserSizeIndex !== -1
      ? convertedUserSizeIndex
      : lowestPricedVariantIndex;

  const allSizes = getAllSizes(sizeOptions, availableSizes);
  const usedAvailableSizes = usedVariant;
  const newAvailableSizes = getNewAvailableSizes(data);

  return (
    <BuyBarVariantsContext.Provider
      value={{
        allSizes,
        mapSizeToVariants,
        availableSizes,
        usedAvailableSizes,
        newAvailableSizes,
        centerOnIndex,
        convertedUserSizeIndex,
        lowestPricedVariantIndex,
      }}
    >
      {children}
    </BuyBarVariantsContext.Provider>
  );
};
